// The poll cadence of the agent-task feed while a task is in flight.
//
// An agent job runs for tens of seconds or for minutes, and one read of the
// feed takes about 3 s in production. A fixed 2 s poll thus kept one request
// open at all times for each open tab. This cadence starts fast, so the first
// status change of a new task shows quickly. Then it slows down to a cap.
//
// The delay is half of the time since the poll epoch, with a floor and a cap.
// Thus each delay is about 1.5 times the previous one:
// 3 s, 3 s, 3 s, 4.5 s, 6.75 s, 10 s, then 15 s for each poll after that.
// The caller sets the epoch again when the set of in-flight tasks changes,
// or when the user starts a task. The cadence then starts fast again.

/** The first delay after the epoch, and the smallest delay. */
export const AGENT_POLL_MIN_MS = 3_000;

/** The largest delay. A long job gets one poll each 15 s. */
export const AGENT_POLL_MAX_MS = 15_000;

/**
 * The delay before the next poll, from the time between the poll epoch and
 * the last fetch. A negative time (the epoch is after the last fetch) gives
 * the floor.
 */
export function nextAgentPollMs(elapsedMs: number): number {
    if (!Number.isFinite(elapsedMs)) return AGENT_POLL_MIN_MS;
    return Math.min(AGENT_POLL_MAX_MS, Math.max(AGENT_POLL_MIN_MS, Math.round(elapsedMs / 2)));
}

/**
 * A key for the set of in-flight tasks (status `queued` or `running`). The
 * key changes when a task starts or stops, and the caller then sets the poll
 * epoch again. The key is an empty string when no task is in flight.
 */
export function inFlightTaskKey(tasks: readonly { id: string; status: string }[]): string {
    return tasks
        .filter((t) => t.status === 'queued' || t.status === 'running')
        .map((t) => t.id)
        .sort()
        .join('|');
}
